import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

export default function ChatWindow({ messages, loading, onSuggest, userName }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  if (messages.length === 0 && !loading) {
    return (
      <div style={s.empty}>
        <div style={s.emptyIcon}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--gold)' }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
        <h3 style={s.emptyTitle}>{userName ? `Hi ${userName}, ask me anything` : 'Ask Nitin'}</h3>
        <p style={s.emptyHint}>Ask anything about your career - internships, choice of practice area, your first job or placement - and get straight, experience-backed guidance from someone who has truly been there.</p>
        <div style={s.emptyChips}>
          {['How do I start my career?', 'What skills should I develop?', 'How to prepare for interviews?'].map((q) => (
            <button key={q} style={s.chip} onClick={() => onSuggest?.(q)}>{q}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={s.window}>
      {messages.map((msg, idx) => {
        const isLast = idx === messages.length - 1;
        const suggestions = (isLast && !loading && msg.suggestedQuestions?.length)
          ? msg.suggestedQuestions : [];
        return (
          <div key={msg.id} style={s.group} className="msg-group">
            {/* User question */}
            <div style={s.userRow}>
              <div style={s.userBubble}>
                <p style={s.userText}>{msg.question}</p>
              </div>
              <div style={s.userAva}>You</div>
            </div>

            {/* AI answer */}
            <div style={s.aiRow}>
              <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
              <div style={s.aiBubble}>
                <div style={s.mdWrapper}>
                  <ReactMarkdown
                    components={{
                      p:      ({ children }) => <p style={s.mdP}>{children}</p>,
                      ul:     ({ children }) => <ul style={s.mdUl}>{children}</ul>,
                      ol:     ({ children }) => <ol style={s.mdOl}>{children}</ol>,
                      li:     ({ children }) => <li style={s.mdLi}>{children}</li>,
                      strong: ({ children }) => <strong style={s.mdStrong}>{children}</strong>,
                      h1:     ({ children }) => <p style={s.mdH}>{children}</p>,
                      h2:     ({ children }) => <p style={s.mdH}>{children}</p>,
                      h3:     ({ children }) => <p style={s.mdH3}>{children}</p>,
                      code:   ({ children }) => <code style={s.mdCode}>{children}</code>,
                    }}
                  >
                    {msg.answer}
                  </ReactMarkdown>
                </div>
                <span style={s.ts}>
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>

            {/* Suggested follow-up questions */}
            {suggestions.length > 0 && (
              <div style={s.suggestRow}>
                <span style={s.suggestLabel}>Ask next</span>
                <div style={s.suggestChips}>
                  {suggestions.map((q, i) => (
                    <button key={i} style={s.suggestChip} onClick={() => onSuggest?.(q)}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {loading && (
        <div style={s.aiRow} className="msg-group">
          <img src="/nitin-potdar-profile.png" alt="Nitin" style={s.aiAva} />
          <div style={{ ...s.aiBubble, padding: '14px 18px' }}>
            <div style={s.typing}>
              {[0, 0.18, 0.36].map((d, i) => (
                <span key={i} style={{ ...s.dot, animationDelay: `${d}s` }} />
              ))}
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

const s = {
  window: {
    flex: 1, overflowY: 'auto', padding: '20px 16px',
    display: 'flex', flexDirection: 'column', gap: '20px',
  },
  empty: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 24px', textAlign: 'center', gap: '12px',
  },
  emptyIcon: {
    width: '56px', height: '56px', borderRadius: '16px',
    background: 'var(--gold-light)', border: '1px solid var(--gold-mid)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  emptyTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '20px', fontWeight: '700', color: 'var(--navy)', marginTop: '4px',
  },
  emptyHint: { fontSize: '13px', color: 'var(--text-3)', maxWidth: '340px', lineHeight: '1.6' },
  emptyChips: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' },
  chip: {
    background: '#fff', border: '1px solid var(--border)',
    color: 'var(--text-2)', fontSize: '12px', fontWeight: '500',
    padding: '6px 12px', borderRadius: '99px', cursor: 'pointer',
  },
  group: { display: 'flex', flexDirection: 'column', gap: '8px' },
  userRow: { display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'flex-end' },
  userBubble: {
    background: 'var(--navy)', color: '#fff',
    padding: '11px 15px', borderRadius: '16px 16px 4px 16px',
    maxWidth: '72%', boxShadow: '0 2px 12px rgba(12,35,64,0.18)',
  },
  userText: { fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: 0 },
  userAva: {
    width: '28px', height: '28px', borderRadius: '50%',
    background: 'var(--navy-2)', color: '#fff',
    fontSize: '8px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  aiRow: { display: 'flex', gap: '8px', alignItems: 'flex-start' },
  aiAva: {
    width: '32px', height: '32px', borderRadius: '50%',
    objectFit: 'cover', objectPosition: 'top center',
    flexShrink: 0, border: '1.5px solid var(--gold-mid)',
    boxShadow: '0 2px 8px rgba(12,35,64,0.12)',
  },
  aiBubble: {
    background: '#fff', border: '1px solid var(--border)',
    padding: '13px 16px', borderRadius: '4px 16px 16px 16px',
    maxWidth: '76%', boxShadow: 'var(--shadow-sm)',
  },
  mdWrapper: { fontSize: '14px', color: 'var(--text)' },
  mdP: { margin: '0 0 8px 0', lineHeight: '1.65' },
  mdUl: { margin: '4px 0 8px 0', paddingLeft: '18px' },
  mdOl: { margin: '4px 0 8px 0', paddingLeft: '18px' },
  mdLi: { lineHeight: '1.65', marginBottom: '3px' },
  mdStrong: { fontWeight: '700', color: 'var(--navy)' },
  mdH:  { fontSize: '14px', fontWeight: '700', margin: '10px 0 4px 0', color: 'var(--navy)', fontFamily: "'Playfair Display', Georgia, serif" },
  mdH3: { fontSize: '13px', fontWeight: '700', margin: '8px 0 4px 0', color: 'var(--navy)' },
  mdCode: {
    background: 'var(--bg)', color: 'var(--navy-2)',
    padding: '1px 5px', borderRadius: '4px',
    fontSize: '12px', fontFamily: 'monospace',
  },
  ts: { fontSize: '10px', color: 'var(--text-3)', display: 'block', marginTop: '8px', textAlign: 'right' },
  suggestRow: { paddingLeft: '38px', display: 'flex', flexDirection: 'column', gap: '6px' },
  suggestLabel: { fontSize: '10px', fontWeight: '600', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  suggestChips: { display: 'flex', flexWrap: 'wrap', gap: '7px' },
  suggestChip: {
    background: '#fff', border: '1px solid var(--gold-mid)',
    color: 'var(--navy)', fontSize: '12px', fontWeight: '500',
    padding: '6px 13px', borderRadius: '99px', cursor: 'pointer',
    textAlign: 'left', lineHeight: '1.4',
    transition: 'background 0.15s, box-shadow 0.15s',
  },
  typing: { display: 'flex', gap: '4px', alignItems: 'center', height: '18px' },
  dot: {
    width: '7px', height: '7px', borderRadius: '50%',
    background: 'var(--gold)', opacity: 0.7,
    animation: 'bounce 1.2s infinite ease-in-out',
    display: 'inline-block',
  },
};
